/**
 * Agent Group Modal
 * Form modal for creating/editing agent groups
 */

import React, { useState, useEffect } from 'react';
import { Modal } from '../shared/Modal';
import { Button } from '../shared/Button';
import type { AgentGroup } from '../../services/agentGroupService';

interface AgentGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (group: Partial<AgentGroup>) => Promise<void>;
  group?: AgentGroup | null;
  nextOrder: number;
}

const TAILWIND_COLORS = [
  { value: 'bg-blue-500', label: 'Blue' },
  { value: 'bg-green-500', label: 'Green' },
  { value: 'bg-purple-500', label: 'Purple' },
  { value: 'bg-red-500', label: 'Red' },
  { value: 'bg-yellow-500', label: 'Yellow' },
  { value: 'bg-pink-500', label: 'Pink' },
  { value: 'bg-indigo-500', label: 'Indigo' },
  { value: 'bg-cyan-500', label: 'Cyan' },
  { value: 'bg-orange-500', label: 'Orange' },
  { value: 'bg-teal-500', label: 'Teal' },
];

export const AgentGroupModal: React.FC<AgentGroupModalProps> = ({
  isOpen,
  onClose,
  onSave,
  group,
  nextOrder,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('bg-blue-500');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (group) {
      setName(group.name);
      setDescription(group.description);
      setColor(group.color);
    } else {
      setName('');
      setDescription('');
      setColor('bg-blue-500');
    }
    setError(null);
  }, [group, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Group name is required');
      return;
    }

    setIsSaving(true);
    try {
      if (group) {
        // Update existing group
        await onSave({ name, description, color });
      } else {
        // Create new group
        await onSave({
          name,
          description,
          color,
          agentIds: [],
          order: nextOrder,
          isCollapsed: false,
          createdBy: 'Neo', // TODO: Get from auth context
        });
      }
      onClose();
    } catch (err) {
      console.error('Failed to save group:', err);
      setError('Failed to save group. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={group ? 'Edit Group' : 'New Group'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="group-name" className="block text-sm font-medium text-gray-700 mb-1">
            Group Name <span className="text-red-500">*</span>
          </label>
          <input
            id="group-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Backend Team"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            maxLength={50}
            required
          />
        </div>

        <div>
          <label htmlFor="group-desc" className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="group-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={3}
            maxLength={200}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
          <div className="grid grid-cols-5 gap-2">
            {TAILWIND_COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setColor(c.value)}
                className={`h-10 rounded-md ${c.value} hover:opacity-80 transition-opacity ${
                  color === c.value ? 'ring-2 ring-offset-2 ring-gray-900' : ''
                }`}
                title={c.label}
              />
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSaving}>
            {isSaving ? 'Saving...' : group ? 'Update Group' : 'Create Group'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
